import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Trophy, Star, Target, Users, BookOpen, Zap, Lock, Check,
  ArrowLeft, Loader2, Award, Flame, Crown, GraduationCap,
  Leaf, Beaker, Droplets, Sun, RefreshCw, Bug, Flower2,
  TreeDeciduous, Recycle, BarChart3, Globe
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Map achievement IDs to icons
const ACHIEVEMENT_ICONS = {
  first_sprout: Leaf,
  green_hand: Leaf,
  soil_scientist: Beaker,
  photosynthesis_initiate: Sun,
  farm_hand: Users,
  companion_cultivator: Users,
  nitrogen_fixer: Beaker,
  rotation_specialist: RefreshCw,
  ipm_practitioner: Bug,
  pollinator_protector: Flower2,
  right_hand: Users,
  permaculture_designer: Leaf,
  microclimate_master: Sun,
  water_cycle_engineer: Droplets,
  carbon_sequester: TreeDeciduous,
  succession_strategist: Target,
  grow_your_plan: BarChart3,
  systems_thinker: Recycle,
  food_forest_architect: TreeDeciduous,
  data_driven_grower: BarChart3,
  community_cultivator: Globe,
  farm_ceo: GraduationCap,
  master_horticulturist: GraduationCap,
  sustainable_ag_champion: Trophy
};

const GamificationPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [levels, setLevels] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState("progress");
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      // Load public data first
      const [achievementsRes, levelsRes, challengesRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/gamification/achievements`),
        axios.get(`${API}/gamification/levels`),
        axios.get(`${API}/gamification/challenges`),
        axios.get(`${API}/gamification/leaderboard`)
      ]);
      
      setAchievements(achievementsRes.data.achievements || []);
      setLevels(levelsRes.data.levels || []);
      setChallenges(challengesRes.data.challenges || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      
      // Load user profile if logged in
      if (user) {
        const profileRes = await axios.get(`${API}/gamification/profile`, { withCredentials: true });
        setProfile(profileRes.data);
      }
    } catch (error) {
      console.error("Failed to load gamification data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkAchievements = async () => {
    if (!user) return;
    setChecking(true);
    try {
      const response = await axios.post(`${API}/gamification/check-achievements`, {}, { withCredentials: true });
      
      if (response.data.new_achievements?.length > 0) {
        response.data.new_achievements.forEach(ach => {
          toast.success(`🏆 Achievement Unlocked: ${ach.name}!`, {
            description: `+${ach.xp} XP - ${ach.stem_concept}`
          });
        });
        loadData(); // Refresh profile
      } else {
        toast.info("Keep designing to unlock more achievements!");
      }
    } catch (error) {
      toast.error("Failed to check achievements");
    } finally {
      setChecking(false);
    }
  };

  const earnedIds = new Set(profile?.achievements_earned || []);
  
  const categoryColors = {
    beginner: "bg-green-100 text-green-800 border-green-300",
    intermediate: "bg-blue-100 text-blue-800 border-blue-300",
    advanced: "bg-purple-100 text-purple-800 border-purple-300",
    expert: "bg-orange-100 text-orange-800 border-orange-300",
    master: "bg-yellow-100 text-yellow-800 border-yellow-300"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-amber-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              Learning & Achievements
            </h1>
          </div>
          
          {user && (
            <Button onClick={checkAchievements} disabled={checking} data-testid="check-achievements-btn">
              {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Check Progress
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* User Profile Card */}
        {user && profile ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-green-600 to-emerald-700 text-white overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="text-6xl">{profile.level_icon}</div>
                  <div className="flex-1">
                    <p className="text-green-100 text-sm">Level {profile.level}</p>
                    <h2 className="text-2xl font-bold">{profile.title}</h2>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-300" />
                        <span className="font-semibold">{profile.xp.toLocaleString()} XP</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-300" />
                        <span>{profile.achievements_earned?.length || 0} Achievements</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>Progress to next level</span>
                        <span>{Math.round(profile.progress_percent)}%</span>
                      </div>
                      <Progress value={profile.progress_percent} className="h-2 bg-green-800" />
                      {profile.xp_to_next > 0 && (
                        <p className="text-xs text-green-200 mt-1">{profile.xp_to_next} XP to next level</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <Card className="mb-8">
            <CardContent className="p-8 text-center">
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-bold mb-2">Track Your Learning Journey</h2>
              <p className="text-muted-foreground mb-4">
                Log in to earn XP, unlock achievements, and learn agricultural STEM concepts.
              </p>
              <Button onClick={login} data-testid="login-btn">Log In to Start Learning</Button>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="progress" className="gap-2">
              <Target className="w-4 h-4" /> Progress
            </TabsTrigger>
            <TabsTrigger value="achievements" className="gap-2">
              <Award className="w-4 h-4" /> Achievements
            </TabsTrigger>
            <TabsTrigger value="learn" className="gap-2">
              <BookOpen className="w-4 h-4" /> Learn
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="gap-2">
              <Crown className="w-4 h-4" /> Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Career Path */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Agricultural Career Path
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {levels.map((level, idx) => {
                      const isCurrentLevel = profile?.level === level.level;
                      const isCompleted = profile?.xp >= level.min_xp;
                      
                      return (
                        <div
                          key={level.level}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                            isCurrentLevel ? 'bg-green-100 border-2 border-green-500' :
                            isCompleted ? 'bg-gray-50' : 'opacity-50'
                          }`}
                        >
                          <div className="text-2xl">{level.icon}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{level.title}</span>
                              {isCompleted && <Check className="w-4 h-4 text-green-600" />}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {level.min_xp.toLocaleString()} XP required
                            </p>
                          </div>
                          <Badge variant={isCurrentLevel ? "default" : "secondary"}>
                            Lvl {level.level}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Daily/Weekly Challenges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    STEM Challenges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {challenges.map(challenge => (
                      <div key={challenge.id} className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={challenge.type === "daily" ? "default" : "secondary"}>
                            {challenge.type}
                          </Badge>
                          <span className="text-sm font-semibold text-orange-600">+{challenge.xp_reward} XP</span>
                        </div>
                        <h4 className="font-semibold">{challenge.name}</h4>
                        <p className="text-sm text-muted-foreground">{challenge.description}</p>
                        <Badge variant="outline" className="mt-2 text-xs">
                          🔬 {challenge.stem_concept}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements">
            <div className="space-y-6">
              {["beginner", "intermediate", "advanced", "expert", "master"].map(category => {
                const categoryAchievements = achievements.filter(a => a.category === category);
                if (categoryAchievements.length === 0) return null;
                
                const earnedInCategory = categoryAchievements.filter(a => earnedIds.has(a.id)).length;
                
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold capitalize flex items-center gap-2">
                        {category === "beginner" && "🌱 Germination Phase"}
                        {category === "intermediate" && "🌿 Vegetative Growth"}
                        {category === "advanced" && "🌸 Flowering Phase"}
                        {category === "expert" && "🍎 Fruiting Phase"}
                        {category === "master" && "🏆 Harvest Mastery"}
                      </h3>
                      <Badge className={categoryColors[category]}>
                        {earnedInCategory}/{categoryAchievements.length}
                      </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryAchievements.map(achievement => {
                        const isEarned = earnedIds.has(achievement.id);
                        const IconComponent = ACHIEVEMENT_ICONS[achievement.id] || Award;
                        
                        return (
                          <motion.div
                            key={achievement.id}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => setSelectedAchievement(achievement)}
                            className="cursor-pointer"
                          >
                            <Card className={`h-full transition-all ${
                              isEarned ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300' : 'opacity-70 hover:opacity-100'
                            }`}>
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-full ${isEarned ? 'bg-yellow-200' : 'bg-gray-200'}`}>
                                    {isEarned ? (
                                      <span className="text-2xl">{achievement.icon}</span>
                                    ) : (
                                      <Lock className="w-6 h-6 text-gray-500" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold">{achievement.name}</h4>
                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                      {achievement.description}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className="text-xs">
                                        +{achievement.xp} XP
                                      </Badge>
                                      <Badge variant="outline" className="text-xs">
                                        🔬 {achievement.stem_concept}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Learn Tab */}
          <TabsContent value="learn">
            <div className="grid md:grid-cols-2 gap-6">
              {achievements.slice(0, 12).map(achievement => (
                <Card key={achievement.id} className="overflow-hidden">
                  <div className={`h-2 ${
                    achievement.category === "beginner" ? "bg-green-500" :
                    achievement.category === "intermediate" ? "bg-blue-500" :
                    achievement.category === "advanced" ? "bg-purple-500" :
                    achievement.category === "expert" ? "bg-orange-500" : "bg-yellow-500"
                  }`} />
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{achievement.icon}</span>
                      <div>
                        <h4 className="font-semibold">{achievement.stem_concept}</h4>
                        <p className="text-xs text-muted-foreground capitalize">{achievement.category} Level</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      {achievement.learning}
                    </p>
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        <strong>Unlock by:</strong> {achievement.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Top Gardeners
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No gardeners on the leaderboard yet!</p>
                    <p className="text-sm">Be the first to earn XP and claim the top spot.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {leaderboard.map((leader, idx) => (
                      <div
                        key={leader.user_id}
                        className={`flex items-center gap-4 p-3 rounded-lg ${
                          idx === 0 ? 'bg-yellow-100 border border-yellow-300' :
                          idx === 1 ? 'bg-gray-100 border border-gray-300' :
                          idx === 2 ? 'bg-orange-100 border border-orange-300' : 'bg-gray-50'
                        }`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center font-bold">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{leader.name}</p>
                          <p className="text-xs text-muted-foreground">{leader.title}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{leader.xp?.toLocaleString()} XP</p>
                          <p className="text-xs text-muted-foreground">{leader.achievement_count} achievements</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Achievement Detail Modal */}
      <AnimatePresence>
        {selectedAchievement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedAchievement(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-4">
                <span className="text-5xl">{selectedAchievement.icon}</span>
                <h3 className="text-xl font-bold mt-2">{selectedAchievement.name}</h3>
                <Badge className={categoryColors[selectedAchievement.category]}>
                  {selectedAchievement.category}
                </Badge>
              </div>
              
              <p className="text-muted-foreground mb-4">{selectedAchievement.description}</p>
              
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  {selectedAchievement.stem_concept}
                </h4>
                <p className="text-sm text-blue-700 mt-1">{selectedAchievement.learning}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <Badge variant="outline">+{selectedAchievement.xp} XP</Badge>
                <Button variant="outline" onClick={() => setSelectedAchievement(null)}>
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GamificationPage;
